import React, { useMemo } from 'react';
import DashboardMapSideTools from './DashboardMapSideTools';
import LiveStreamModal from './LiveStreamModal';
import DashboardStatusHud from './DashboardStatusHud';
import DashboardMapStyleRescueBar from './DashboardMapStyleRescueBar';
import DashboardMemberToolbar from './DashboardMemberToolbar';
import { getMemberExpandKey } from '../utils/dashboard/memberTreeHelpers';
import { missionIdMatchesMember } from '../utils/dashboard/deviceIdentity';
import DashboardMapStatusPanel from './DashboardMapStatusPanel';
import DashboardMapCenterDot from './DashboardMapCenterDot';
import DashboardMapLoadingOverlay from './DashboardMapLoadingOverlay';
import DashboardMapTopAlertStack from './DashboardMapTopAlertStack';
import TrackDownloadModal from './TrackDownloadModal';
import DashboardSidebarDragHandle from './DashboardSidebarDragHandle';
import DashboardMobilePanelShell from './DashboardMobilePanelShell';
import { downloadAuthorizedTeamKml } from '../utils/downloadTeamKml';
import DashboardMemberTreeBlock from './DashboardMemberTreeBlock';
import DashboardMapLocateStack, { DashboardDeviceLocateButton } from './DashboardMapLocateStack';
import DashboardBasemapAttribution from './DashboardBasemapAttribution';
import { ONLINE_TIMEOUT } from '../utils/dashboard/constants';
import { flyToActiveOrOnlineDevice } from '../utils/dashboard/locateCommanderHelpers';

/**
 * 指挥页已授权后的主工作区（桌面/移动布局）
 */
export default function DashboardWorkspaceLayout({
  isMobile,
  mapContainer,
  mapRef,
  membersRef,
  members,
  drawingToolsActiveRef,
  mapLoadingState,
  isEarthAnimationActive,
  showLeftPanel,
  leftPanelVisible,
  mapStatus,
  rescueTargets,
  rescueTracks,
  mapStyles,
  currentMapStyleId,
  styleModeAvailability,
  onlineNotifications,
  activeMemberId,
  networkQuality,
  devicePerformance,
  refreshStatus,
  lastRefreshTime,
  sidebarRef,
  collapsed,
  sidebarWidth,
  sidebarMotionEnabled = true,
  startTime,
  endTime,
  showMemberNames,
  hideInvalid,
  showOnlineOnly,
  filterMembers,
  missionMode,
  missionMemberIds,
  setMissionMode,
  setMissionMemberIds,
  memberPanelTab = 'all',
  setMemberPanelTab,
  playbackTargetNames = [],
  setPlaybackTargetNames,
  playbackSpeed = 10,
  setPlaybackSpeed,
  trackData = {},
  trackColors = {},
  showTracks = {},
  toggleTrack,
  startRealTimeTrackDrawing = null,
  stopTrackFeatures = null,
  realTimeTrackDrawing = null,
  getMap,
  filteredCount,
  totalMemberCount = 0,
  onlineMemberCount = 0,
  memberSearchQuery = '',
  onMemberSearchQueryChange,
  refreshInterval,
  trackModeAndPlaybackUI,
  playbackPanelUI,
  memberTreeBlockProps,
  commandFeatures = {},
  mobileContainerRef,
  mobileContainerHeight,
  isDragging,
  allTracksVisible,
  showDownload,
  announcement,
  showAnnouncement,
  fieldMessageBanners,
  maxVisibleFieldBanners,
  onCloseFieldMessageBanner,
  onFlyToFieldMessage,
  trackDownloadModal,
  showLiveStream,
  currentStreamMember,
  // actions
  stopEarthAnimation,
  currentBasemapId,
  activeOverlayIds = [],
  loadRescueData,
  clearRescueData,
  toggleLeftPanel,
  switchMapStyle,
  showRescueTargetsList,
  showRescueTracksList,
  onRescueSearchSetTarget = null,
  renderBasemapLayerControl,
  locateCommander,
  fetchDevices,
  onSidebarExpand,
  handleDragMouseDown,
  handleResetDashboardConfig,
  handleReplayEarth,
  setShowMemberNames,
  handleShowOnlineTracks,
  handleDrawOnlineTracks,
  setHideInvalid,
  setShowOnlineOnly,
  onExportKml,
  setStartTime,
  setEndTime,
  handleRefreshIntervalChange,
  handleMobileDragStart,
  handleExpandClick,
  handleCollapseClick,
  toggleAllTracks,
  closeAnnouncement,
  setTrackDownloadModal,
  confirmDownloadMemberTrack,
  closeLiveStream,
  onOnlineNotificationClick,
  missionPickIds = [],
  onMissionPickIdsChange,
  onJoinMission,
  missionGroups = [],
  onMissionGroupsChange,
  onRemoveMissionMember,
  onEndMission,
  missions = [],
  activeMissionId = null,
  onSelectMission,
  onCreateMission,
  onRenameMission,
  onCloseMission,
  onToggleMissionRecordTracks,
  missionBadgeCount = null,
  isMissionMemberOnline,
  getMissionMemberPresence,
  onFlyToMissionMember,
  voicePulseIds,
  notesByKey,
  onSaveMemberNote,
  onSendLocationToMember,
  onSendMessageToMember,
  streamingMembers,
  openLiveStream,
  getMessagesForMember,
  onMissionChatOutbound,
  onMissionChatOpenChange,
  onMissionChatMessageDeleted = null,
  historyEpoch = 0,
  getBasemapId = null
}) {
  const requestMapSelectSpace = () => {
    if (!isMobile) return;
    handleCollapseClick?.();
    try {
      window.dispatchEvent(new CustomEvent('zhxt-collapse-map-side-tools'));
    } catch (_) { /* ignore */ }
    setTimeout(() => {
      try { mapRef.current?.resize?.(); } catch (__) { /* ignore */ }
    }, 120);
  };

  const missionIdSet = useMemo(
    () => new Set(Array.isArray(missionMemberIds) ? missionMemberIds : []),
    [missionMemberIds]
  );
  const missionMembers = useMemo(() => {
    // Prefer full members so offline/filtered-out people stay in mission groups (req 8)
    const list = Array.isArray(members) && members.length
      ? members
      : (Array.isArray(filterMembers) ? filterMembers : []);
    const ids = Array.from(missionIdSet);
    return list.filter((m) => ids.some((id) => missionIdMatchesMember(id, m) || missionIdSet.has(getMemberExpandKey(m))));
  }, [filterMembers, members, missionIdSet]);

  const onMissionPickToggle = (id) => {
    if (!id || !onMissionPickIdsChange) return;
    const cur = Array.isArray(missionPickIds) ? missionPickIds : [];
    if (cur.includes(id)) onMissionPickIdsChange(cur.filter((x) => x !== id));
    else onMissionPickIdsChange([...cur, id]);
  };
  const onMissionPickMany = (ids, checked) => {
    if (!onMissionPickIdsChange) return;
    const cur = new Set(Array.isArray(missionPickIds) ? missionPickIds : []);
    (ids || []).forEach((id) => {
      if (!id) return;
      if (checked) cur.add(id);
      else cur.delete(id);
    });
    onMissionPickIdsChange(Array.from(cur));
  };

  const statusPanel = (variant) => (
    <DashboardMapStatusPanel
      variant={variant}
      visible={showLeftPanel}
      animVisible={leftPanelVisible}
      mapStatus={mapStatus}
      rescueTargets={rescueTargets}
      rescueTracks={rescueTracks}
      onToggle={toggleLeftPanel}
    />
  );

  const styleRescueBar = (variant) => (
    <DashboardMapStyleRescueBar
      variant={variant}
      mapStyles={mapStyles}
      currentMapStyleId={currentMapStyleId}
      currentBasemapId={currentBasemapId}
      styleModeAvailability={styleModeAvailability}
      onSwitchMapStyle={switchMapStyle}
      rescueTargets={rescueTargets}
      rescueTracks={rescueTracks}
      onToggleRescue={(hasData) => {
        if (hasData) clearRescueData();
        else loadRescueData();
      }}
      onShowTargetsList={showRescueTargetsList}
      onShowTracksList={showRescueTracksList}
      getMap={getMap}
      onRescueSearchSetTarget={onRescueSearchSetTarget}
    />
  );

  const statusHud = (variant) => (
    <DashboardStatusHud
      variant={variant}
      members={members}
      activeMemberId={activeMemberId}
      networkQuality={networkQuality}
      devicePerformance={devicePerformance}
      refreshStatus={refreshStatus}
      lastRefreshTime={lastRefreshTime}
      onRefresh={fetchDevices}
    />
  );

  const topAlertStack = (
    <DashboardMapTopAlertStack
      announcementVisible={!!announcement && showAnnouncement}
      announcement={announcement}
      onCloseAnnouncement={closeAnnouncement}
      fieldMessageBanners={fieldMessageBanners}
      maxVisibleFieldBanners={maxVisibleFieldBanners}
      onCloseFieldMessageBanner={onCloseFieldMessageBanner}
      onFlyToFieldMessage={onFlyToFieldMessage}
      onlineNotifications={onlineNotifications}
      onOnlineNotificationClick={onOnlineNotificationClick}
    />
  );

  return (
    <div style={{
      width: '100vw',
      height: 'calc(100vh - var(--header-total-height))',
      display: 'flex',
      flexDirection: 'column',
      background: '#eaf3fa'
    }}>
      {!isMobile ? (
        <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
          <div style={{
            position: 'relative',
            flex: 1,
            minWidth: 0,
            overflow: 'hidden'
          }}>
            <div
              ref={mapContainer}
              style={{ width: '100%', height: '100%', position: 'relative' }}
              className="map-container"
            />
            <DashboardMapCenterDot visible={!isEarthAnimationActive} />
            <DashboardMapSideTools
              commandFeatures={commandFeatures}
              mapLoadingState={mapLoadingState}
              mapRef={mapRef}
              members={members}
              drawingToolsActiveRef={drawingToolsActiveRef}
              stopEarthAnimation={stopEarthAnimation}
              loadRescueData={loadRescueData}
              isMobile={isMobile}
              onRequestMapSelectSpace={requestMapSelectSpace}
            />
            <DashboardMapLoadingOverlay
              mapLoadingState={mapLoadingState}
              mapReady={!!mapRef.current}
              isEarthAnimationActive={isEarthAnimationActive}
            />
            <DashboardBasemapAttribution
              basemapId={currentBasemapId}
            />
            {statusPanel('desktop')}
            {styleRescueBar('desktop')}
            <DashboardMapLocateStack
              bottom={36}
              zIndex={20}
              basemapControl={commandFeatures.basemap !== false ? renderBasemapLayerControl(false) : null}
              onLocateCommander={() => locateCommander()}
            />
            {statusHud('desktop')}
            {topAlertStack}
          </div>

          <div
            ref={sidebarRef}
            style={{
              width: collapsed ? 40 : sidebarWidth,
              minWidth: collapsed ? 40 : 320,
              maxWidth: collapsed ? 40 : 800,
              background: collapsed ? 'transparent' : '#eaf3fb',
              borderLeft: collapsed ? 'none' : '1px solid #ccc',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              // 首屏禁用 transition，避免刷新时宽度从默认跳到存档产生「伸缩」
              transition: sidebarMotionEnabled
                ? 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1), background 0.3s cubic-bezier(0.4, 0, 0.2, 1), border 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                : 'none',
              position: 'relative'
            }}
          >
            <DashboardSidebarDragHandle
              collapsed={collapsed}
              sidebarWidth={sidebarWidth}
              onExpand={onSidebarExpand}
              onDragMouseDown={handleDragMouseDown}
            />
            {!collapsed && (
              <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
                <DashboardMemberToolbar
                  startTime={startTime}
                  endTime={endTime}
                  onStartTimeChange={setStartTime}
                  onEndTimeChange={setEndTime}
                  onResetConfig={handleResetDashboardConfig}
                  onReplayEarth={handleReplayEarth}
                  showMemberNames={showMemberNames}
                  onToggleMemberNames={() => setShowMemberNames(!showMemberNames)}
                  onShowOnlineTracks={handleShowOnlineTracks}
                  onDrawOnlineTracks={handleDrawOnlineTracks}
                  hideInvalid={hideInvalid}
                  showOnlineOnly={showOnlineOnly}
                  onHideInvalidChange={setHideInvalid}
                  onShowOnlineOnlyChange={setShowOnlineOnly}
                  filteredCount={filteredCount}
                  totalCount={totalMemberCount}
                  onlineCount={onlineMemberCount}
                  searchQuery={memberSearchQuery}
                  onSearchQueryChange={onMemberSearchQueryChange}
                  refreshInterval={refreshInterval}
                  onRefreshIntervalChange={handleRefreshIntervalChange}
                  networkQuality={networkQuality}
                  trackModeAndPlaybackUI={trackModeAndPlaybackUI}
                  playbackPanelUI={playbackPanelUI}
                  commandFeatures={commandFeatures}
                  onToggleAllTracks={toggleAllTracks}
                  allTracksVisible={allTracksVisible}
                  showDownload={showDownload && commandFeatures.exportTrack !== false}
                  onExportKml={onExportKml || downloadAuthorizedTeamKml}
                  filterMembers={filterMembers || members}
                  missionMode={missionMode}
                  missionMemberIds={missionMemberIds}
                  onMissionModeChange={setMissionMode}
                  onMissionMemberIdsChange={setMissionMemberIds}
                  panelTab={memberPanelTab}
                  onPanelTabChange={setMemberPanelTab}
                  missionPickIds={missionPickIds}
                  onMissionPickIdsChange={onMissionPickIdsChange}
                  onJoinMission={onJoinMission}
                  missionGroups={missionGroups}
                  onMissionGroupsChange={onMissionGroupsChange}
                  onRemoveMissionMember={onRemoveMissionMember}
                  onEndMission={onEndMission}
            missions={missions}
            activeMissionId={activeMissionId}
            onSelectMission={onSelectMission}
            onCreateMission={onCreateMission}
            onRenameMission={onRenameMission}
            onCloseMission={onCloseMission}
            onToggleMissionRecordTracks={onToggleMissionRecordTracks}
            missionBadgeCount={missionBadgeCount}
                  missionMembers={missionMembers}
                  isMissionMemberOnline={isMissionMemberOnline}
                  getMissionMemberPresence={getMissionMemberPresence}
                  onFlyToMissionMember={onFlyToMissionMember}
                  voicePulseIds={voicePulseIds}
                  notesByKey={notesByKey}
                  onSaveMemberNote={onSaveMemberNote}
                  onSendLocationToMember={onSendLocationToMember}
                  onSendMessageToMember={onSendMessageToMember}
                  streamingMembers={streamingMembers}
                  openLiveStream={openLiveStream}
                  getMessagesForMember={getMessagesForMember}
                  onMissionChatOutbound={onMissionChatOutbound}
                  onMissionChatOpenChange={onMissionChatOpenChange}
                  onMissionChatMessageDeleted={onMissionChatMessageDeleted}
                  historyEpoch={historyEpoch}
                  getBasemapId={getBasemapId}
                  playbackTargetNames={playbackTargetNames}
                  onPlaybackTargetNamesChange={setPlaybackTargetNames}
                  playbackSpeed={playbackSpeed}
                  onPlaybackSpeedChange={setPlaybackSpeed}
                  trackData={trackData}
                  trackColors={trackColors}
                  showTracks={showTracks}
                  onToggleTrack={toggleTrack}
                  startRealTimeTrackDrawing={startRealTimeTrackDrawing}
                  stopTrackFeatures={stopTrackFeatures}
                  realTimeTrackDrawing={realTimeTrackDrawing}
                  getMap={getMap}
                  />
                {memberPanelTab === 'all' && (
                  <DashboardMemberTreeBlock
                    variant="desktop"
                    showTitle={false}
                    {...memberTreeBlockProps}
                    missionSelectable
                    missionPickIds={missionPickIds}
                    onMissionPickToggle={onMissionPickToggle}
                    onMissionPickMany={onMissionPickMany}
                    missionMemberIdSet={missionIdSet}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          <div
            className="map-container zhxt-dashboard-map-mobile"
            style={{
              position: 'relative',
              height: `${(1 - mobileContainerHeight) * 100}vh`,
              minHeight: '20vh',
              maxHeight: '90vh',
              flex: 'none',
              overflow: 'hidden'
            }}
          >
            <div
              ref={mapContainer}
              style={{
                width: '100%',
                height: '100%',
                minHeight: '100%',
                maxHeight: '100%',
                position: 'relative'
              }}
              className="map-container"
            />
            <DashboardMapCenterDot visible={!isEarthAnimationActive} />
            <DashboardMapSideTools
              commandFeatures={commandFeatures}
              mapLoadingState={mapLoadingState}
              mapRef={mapRef}
              members={members}
              drawingToolsActiveRef={drawingToolsActiveRef}
              stopEarthAnimation={stopEarthAnimation}
              loadRescueData={loadRescueData}
              isMobile={isMobile}
              onRequestMapSelectSpace={requestMapSelectSpace}
            />
            <DashboardMapLoadingOverlay
              mapLoadingState={mapLoadingState}
              mapReady={!!mapRef.current}
              isEarthAnimationActive={isEarthAnimationActive}
            />
            <DashboardBasemapAttribution
              basemapId={currentBasemapId}
            />
            <DashboardMapLocateStack
              bottom={80}
              zIndex={30}
              basemapControl={commandFeatures.basemap !== false ? renderBasemapLayerControl(true) : null}
              onLocateCommander={() => locateCommander()}
            />
            {statusPanel('mobile')}
            {styleRescueBar('mobile')}
            <DashboardDeviceLocateButton
              bottom={18}
              zIndex={20}
              onLocateDevice={() => {
                flyToActiveOrOnlineDevice({
                  getMap: () => mapRef.current,
                  members: membersRef.current || members,
                  activeMemberId,
                  onlineTimeout: ONLINE_TIMEOUT,
                  basemapId: currentBasemapId,
                  stopEarthAnimation
                });
              }}
            />
            {statusHud('mobile')}
            {topAlertStack}
          </div>

          <DashboardMobilePanelShell
            containerRef={mobileContainerRef}
            heightRatio={mobileContainerHeight}
            isDragging={isDragging}
            onDragStart={handleMobileDragStart}
            onExpand={handleExpandClick}
            onCollapse={handleCollapseClick}
            onLocate={() => locateCommander()}
          >
            <div style={{
              display: 'flex',
              flex: 1,
              minHeight: 0,
              flexDirection: 'column',
              width: '100%',
              overflowY: 'auto'
            }}>
              <div style={{ padding: '8px 12px 0', background: '#fff', flexShrink: 0 }}>
                <DashboardMemberToolbar
                  startTime={startTime}
                  endTime={endTime}
                  onStartTimeChange={setStartTime}
                  onEndTimeChange={setEndTime}
                  onResetConfig={handleResetDashboardConfig}
                  onReplayEarth={handleReplayEarth}
                  showMemberNames={showMemberNames}
                  onToggleMemberNames={() => setShowMemberNames(!showMemberNames)}
                  onShowOnlineTracks={handleShowOnlineTracks}
                  onDrawOnlineTracks={handleDrawOnlineTracks}
                  hideInvalid={hideInvalid}
                  showOnlineOnly={showOnlineOnly}
                  onHideInvalidChange={setHideInvalid}
                  onShowOnlineOnlyChange={setShowOnlineOnly}
                  filteredCount={filteredCount}
                  totalCount={totalMemberCount}
                  onlineCount={onlineMemberCount}
                  searchQuery={memberSearchQuery}
                  onSearchQueryChange={onMemberSearchQueryChange}
                  refreshInterval={refreshInterval}
                  onRefreshIntervalChange={handleRefreshIntervalChange}
                  networkQuality={networkQuality}
                  trackModeAndPlaybackUI={trackModeAndPlaybackUI}
                  playbackPanelUI={playbackPanelUI}
                  commandFeatures={commandFeatures}
                  onToggleAllTracks={toggleAllTracks}
                  allTracksVisible={allTracksVisible}
                  showDownload={showDownload && commandFeatures.exportTrack !== false}
                  onExportKml={onExportKml || downloadAuthorizedTeamKml}
                  filterMembers={filterMembers || members}
                  missionMode={missionMode}
                  missionMemberIds={missionMemberIds}
                  onMissionModeChange={setMissionMode}
                  onMissionMemberIdsChange={setMissionMemberIds}
                  panelTab={memberPanelTab}
                  onPanelTabChange={setMemberPanelTab}
                  missionPickIds={missionPickIds}
                  onMissionPickIdsChange={onMissionPickIdsChange}
                  onJoinMission={onJoinMission}
                  missionGroups={missionGroups}
                  onMissionGroupsChange={onMissionGroupsChange}
                  onRemoveMissionMember={onRemoveMissionMember}
                  onEndMission={onEndMission}
                  missions={missions}
                  activeMissionId={activeMissionId}
                  onSelectMission={onSelectMission}
                  onCreateMission={onCreateMission}
                  onRenameMission={onRenameMission}
                  onCloseMission={onCloseMission}
                  onToggleMissionRecordTracks={onToggleMissionRecordTracks}
                  missionBadgeCount={missionBadgeCount}
                  missionMembers={missionMembers}
                  isMissionMemberOnline={isMissionMemberOnline}
                  getMissionMemberPresence={getMissionMemberPresence}
                  onFlyToMissionMember={onFlyToMissionMember}
                  voicePulseIds={voicePulseIds}
                  notesByKey={notesByKey}
                  onSaveMemberNote={onSaveMemberNote}
                  onSendLocationToMember={onSendLocationToMember}
                  onSendMessageToMember={onSendMessageToMember}
                  streamingMembers={streamingMembers}
                  openLiveStream={openLiveStream}
                  getMessagesForMember={getMessagesForMember}
                  onMissionChatOutbound={onMissionChatOutbound}
                  onMissionChatOpenChange={onMissionChatOpenChange}
                  onMissionChatMessageDeleted={onMissionChatMessageDeleted}
                  historyEpoch={historyEpoch}
                  getBasemapId={getBasemapId}
                  playbackTargetNames={playbackTargetNames}
                  onPlaybackTargetNamesChange={setPlaybackTargetNames}
                  playbackSpeed={playbackSpeed}
                  onPlaybackSpeedChange={setPlaybackSpeed}
                  trackData={trackData}
                  trackColors={trackColors}
                  showTracks={showTracks}
                  onToggleTrack={toggleTrack}
                  startRealTimeTrackDrawing={startRealTimeTrackDrawing}
                  stopTrackFeatures={stopTrackFeatures}
                  realTimeTrackDrawing={realTimeTrackDrawing}
                  getMap={getMap}
                  />
              </div>
              {memberPanelTab === 'all' && (
                <DashboardMemberTreeBlock
                  variant="mobile"
                  showTitle={false}
                  {...memberTreeBlockProps}
                  missionSelectable
                  missionPickIds={missionPickIds}
                  onMissionPickToggle={onMissionPickToggle}
                  onMissionPickMany={onMissionPickMany}
                  missionMemberIdSet={missionIdSet}
                />
              )}
            </div>
          </DashboardMobilePanelShell>
        </>
      )}

      <TrackDownloadModal
        modal={trackDownloadModal}
        onChange={setTrackDownloadModal}
        onCancel={() => {
          if (trackDownloadModal.loading) return;
          setTrackDownloadModal((prev) => ({ ...prev, visible: false }));
        }}
        onOk={confirmDownloadMemberTrack}
      />

      <LiveStreamModal
        open={showLiveStream}
        member={currentStreamMember}
        onClose={closeLiveStream}
      />
    </div>
  );
}
